import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import MyTable from "../../components/Schedule/MyTable";
import ApiService from "../../functions/apiService";
import { useSelector, useDispatch } from "react-redux";
import {
  setSystemLogReducer,
  setSystemLogDate,
  setSystemLogDDLEmployee,
  setSystemLogDDLEmployeeLogType,
  setSystemLogDDLEmployeeLogSeverity,
} from "../../store/systemLogReducer";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { getGridStringOperators } from "@mui/x-data-grid";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment-timezone";
import Autocomplete from "@mui/material/Autocomplete";
import PaginationComponent from "../../components/Pagination/Pagination";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";

const containsOnly = getGridStringOperators().filter(
  (operator) => operator.value === "contains"
);

const columns = [
  {
    field: "Log_Time",
    headerName: "Timestamp",
    flex: 1,
    minWidth: 160,
    hideable: false,
    filterOperators: containsOnly,
    renderCell: (params) => params?.row?.Log_Time,
  },
  {
    field: "LoggedBy",
    headerName: "Logged By",
    flex: 1,
    minWidth: 200,
    filterOperators: containsOnly,
    valueGetter: (params) =>
      params.row?.log_by_emp?.employee_assignment?.EA_Rank != null
        ? params.row?.log_by_emp?.employee_assignment?.EA_Rank +
          " " +
          params.row?.log_by_emp?.fullName
        : params.row?.log_by_emp?.fullName,
  },
  {
    field: "Log_Severity",
    headerName: "Sev",
    flex: 1,
    minWidth: 60,
    maxWidth:80,
    filterOperators: containsOnly,
    renderCell: (params) => (
      <Chip
        size="small"
        label={params.value}
        sx={{
          backgroundColor: "rgba(255, 165, 0, 0.26)",
          color: "#000000",
          border: "1px solid rgba(255, 165, 0, 0.28)",
          borderRadius: "20px",
          fontWeight: 500,
        }}
      />
    ),
  },
  {
    field: "Log_Type",
    headerName: "Type",
    flex: 1,
    minWidth: 120,
    filterOperators: containsOnly,
  },
  {
    field: "User",
    headerName: "User",
    flex: 1,
    minWidth: 220,
    filterOperators: containsOnly,
    renderCell: (params) =>
      params?.row?.log_emp && (
        <Chip
          sx={{
            height: "auto",
            width: "100%",
            backgroundColor: "rgba(52, 152, 219, 0.16)",
            border: "1px solid rgba(52, 152, 219, 0.2)",
            "& .MuiChip-label": {
              display: "block",
              whiteSpace: "normal",
              padding: "6px 8px",
            },
          }}
          label={
            params.row?.log_emp?.employee_assignment?.EA_Rank != null
              ? params.row?.log_emp?.employee_assignment?.EA_Rank +
                " " +
                params.row?.log_emp?.fullName
              : params.row?.log_emp?.fullName
          }
        />
      ),
  },
  {
    field: "Log_User_Agent",
    headerName: "Browser/Device",
    flex: 1,
    minWidth: 260,
    filterable: false,
    renderCell: (params) => (
      <Tooltip title={params.row.Log_User_Agent}>
        <Typography className="text-wrap">
          {params.row.user_agent
            ? params.row.user_agent.User_Agent_Simple
            : params.row.Log_User_Agent}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "Log_Text",
    headerName: "System Message",
    flex: 1,
    minWidth: 300,
    filterOperators: containsOnly,
    renderCell: (params) => (
      <div style={{ padding: "16px 0px" }}>{params.value}</div>
    ),
  },
];

const BRAND = "#BF1D2D";
const FIELD_BORDER = "#E2E6EA";
const FIELD_BORDER_HOVER = "#B7BDC3";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    transition: "background-color 120ms ease, box-shadow 120ms ease",
    paddingY: 0,
    "& fieldset": {
      borderColor: FIELD_BORDER,
      transition: "border-color 120ms ease",
    },
    "&:hover": {
      backgroundColor: "#FAFBFC",
      "& fieldset": { borderColor: FIELD_BORDER_HOVER },
    },
    "&.Mui-focused": {
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: BRAND,
        borderWidth: "1.5px",
      },
      boxShadow: "0 0 0 3px rgba(191, 29, 45, 0.10)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#6B757C",
    fontSize: 14,
    "&.Mui-focused": { color: BRAND },
  },
  "& .MuiAutocomplete-input": {
    padding: "8px 6px !important",
    fontSize: 14,
  },
  "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": {
    color: "#6B757C",
  },
};

const autocompletePopupSx = {
  paper: {
    sx: {
      borderRadius: "10px",
      marginTop: "6px",
      boxShadow:
        "0 12px 32px rgba(15, 23, 30, 0.10), 0 0 0 1px rgba(15, 23, 30, 0.04)",
      overflow: "hidden",
      "& .MuiAutocomplete-listbox": {
        padding: 0,
        maxHeight: 320,
        "& .MuiAutocomplete-option": {
          padding: "10px 14px",
          fontSize: 14,
          borderBottom: "1px solid #F2F4F6",
          transition: "background-color 80ms ease",
          "&:last-of-type": { borderBottom: "none" },
          "&[aria-selected='true']": {
            backgroundColor: "rgba(191, 29, 45, 0.08)",
            fontWeight: 600,
          },
          '&.Mui-focused, &[data-focus="true"]': {
            backgroundColor: "rgba(191, 29, 45, 0.06)",
          },
          "&[aria-selected='true'].Mui-focused": {
            backgroundColor: "rgba(191, 29, 45, 0.12)",
          },
        },
      },
    },
  },
};

const datePickerSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    paddingRight: "8px",
    transition: "background-color 120ms ease, box-shadow 120ms ease",
    "& fieldset": {
      borderColor: FIELD_BORDER,
      transition: "border-color 120ms ease",
    },
    "&:hover": {
      backgroundColor: "#FAFBFC",
      "& fieldset": { borderColor: FIELD_BORDER_HOVER },
    },
    "&.Mui-focused": {
      "& fieldset": { borderColor: BRAND, borderWidth: "1.5px" },
      boxShadow: "0 0 0 3px rgba(191, 29, 45, 0.10)",
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
      fontSize: 14,
    },
    "& .MuiButtonBase-root": {
      color: "#6B757C",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#6B757C",
    fontSize: 14,
    "&.Mui-focused": { color: BRAND },
  },
};

const System = () => {
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.systemLogReducer.Data);
  const date = useSelector((state) => state.systemLogReducer.Date);
  const DDLEmployee = useSelector(
    (state) => state.systemLogReducer.DDLEmployee
  );
  const EmployeeLogType = useSelector(
    (state) => state.systemLogReducer.EmployeeLogType
  );
  const EmployeeLogSeverity = useSelector(
    (state) => state.systemLogReducer.EmployeeLogSeverity
  );
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [debugWorking, setDebugWorking] = useState(false);
  const [filter, setFilter] = useState({
    LogTime: moment(new Date()).format("DD-MM-YYYY"),
    LogEmpCode: "",
    LogType: "",
    LogSeverity: "",
    LogEmpText: "",
    field: "",
    type: "",
    isDebug: false,
  });
  const [dateValue, setDateValue] = useState(date);
  const [changedDate, setChangedDate] = useState("");

  const loadData = (page = 1) => {
    let Query = "?page=" + page;
    for (let key in filter) {
      Query += "&" + key + "=" + filter[key];
    }
    setTableLoading(true);
    ApiService.get("/getSystemLog" + Query, {}, "getSystemLog")
      .then(({ data }) => {
        dispatch(setSystemLogReducer(data));
        setTableLoading(false);
        setLoading(false);
      })
      .catch(() => {
        setTableLoading(false);
        setLoading(false);
        toast.error("Error");
      });
  };

  useEffect(() => {
    if (DDLEmployee.length == 0) {
      ApiService.get("/getEmployeeDDL").then(({ data }) => {
        dispatch(setSystemLogDDLEmployee(data));
      });
    }
    if (EmployeeLogType.length == 0) {
      ApiService.get("/getEmployeeLogTypeDDL").then(({ data }) => {
        dispatch(setSystemLogDDLEmployeeLogType(data));
      });
    }
    if (EmployeeLogSeverity.length == 0) {
      ApiService.get("/getEmployeeLogSeverityDDL").then(({ data }) => {
        dispatch(setSystemLogDDLEmployeeLogSeverity(data));
      });
    }
    setLoading(false);
    setTableLoading(false);
    setDateValue(new Date());
  }, []);

  useEffect(() => {
    loadData(1);
  }, [filter]);

  const setPaginationModel = (e) => {
    loadData(e.page + 1);
  };

  const changeDate = (date) => {
    setChangedDate(date);
  };

  const onAccept = (date) => {
    if (date != null) {
      dispatch(setSystemLogDate(date));
      setFilter((pre) => ({
        ...pre,
        LogTime: moment(date).format("DD-MM-YYYY"),
      }));
    } else {
      setFilter((pre) => ({ ...pre, LogTime: "" }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key == "Enter") {
      if (!isNaN(new Date(changedDate))) {
        dispatch(setSystemLogDate(changedDate));
        setFilter((pre) => ({
          ...pre,
          LogTime: moment(changedDate).format("DD-MM-YYYY"),
        }));
      } else {
        toast.error("Date is invalid");
      }
    }
  };

  const changeFilter = (e, value, type) => {
    if (value != null) {
      setFilter((pre) => ({ ...pre, [type]: value.value }));
    } else {
      setFilter((pre) => ({ ...pre, [type]: "" }));
    }
  };

  const handleDebugChange = (e) => {
    const checked = e.target.checked;
    setDebugWorking(checked);
    setFilter((pre) => ({ ...pre, isDebug: checked }));
  };

  const PageChanged = (e, page) => {
    loadData(page);
  };

  const handleSortModelChange = (value) => {
    setFilter((pre) => ({
      ...pre,
      field: value.length == 0 ? filter.field : value[0].field,
      type: value.length == 0 ? [] : value[0].sort,
    }));
  };

  return (
    <Card
      sx={{
        mt: 2,
        p: { xs: 2, md: 3 },
        borderRadius: "14px",
        border: "1px solid #ECEEF1",
        boxShadow: "0 2px 12px rgba(15, 23, 30, 0.04)",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "#1F2A30", lineHeight: 1.2 }}
          >
            View System Log
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#6B757C", mt: 0.5, fontSize: 13 }}
          >
            Filter activity by date, employee, type, and severity
          </Typography>
        </Box>
        <FormControlLabel
          sx={{
            m: 0,
            px: 1.5,
            py: 0.5,
            borderRadius: "10px",
            border: `1px solid ${debugWorking ? "rgba(191, 29, 45, 0.35)" : FIELD_BORDER}`,
            backgroundColor: debugWorking ? "rgba(191, 29, 45, 0.06)" : "#FFFFFF",
            transition: "background-color 120ms ease, border-color 120ms ease",
          }}
          control={
            <Checkbox
              checked={debugWorking}
              onChange={handleDebugChange}
              sx={{
                color: "#9AA1A8",
                "&.Mui-checked": { color: BRAND },
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontWeight: 600,
                letterSpacing: "0.5px",
                fontSize: 13,
                color: debugWorking ? BRAND : "#48535B",
              }}
            >
              DEBUG LOGS
            </Typography>
          }
        />
      </Stack>

      <Grid
        container
        spacing={2}
        columns={{ xs: 4, sm: 4, md: 8, lg: 8 }}
        sx={{ mb: 3 }}
      >
        <Grid item xs={4} md={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              onAccept={onAccept}
              format={"MM/dd/yyyy"}
              label={"Date"}
              maxDate={new Date()}
              value={dateValue}
              onChange={changeDate}
              slotProps={{
                actionBar: { actions: ["clear", "today"] },
                textField: {
                  onKeyDown: handleKeyDown,
                  fullWidth: true,
                },
              }}
              sx={datePickerSx}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={4} md={2}>
          <Autocomplete
            disableListWrap
            disablePortal
            slotProps={autocompletePopupSx}
            onChange={(e, value) => changeFilter(e, value, "LogEmpCode")}
            options={DDLEmployee.map((item) => ({
              label:
                item.Emp_Code +
                " " +
                (item.EA_Rank != null ? item.EA_Rank + " " : "") +
                item.fullName,
              value: item.Emp_Code,
            }))}
            renderInput={(params) => (
              <TextField {...params} sx={fieldSx} label="Log Employee" />
            )}
          />
        </Grid>
        <Grid item xs={4} md={2}>
          <Autocomplete
            disableListWrap
            disablePortal
            slotProps={autocompletePopupSx}
            value={`${filter.LogType}`}
            onChange={(e, value) => changeFilter(e, value, "LogType")}
            options={EmployeeLogType.map((item) => ({
              label: item,
              value: item,
            }))}
            renderInput={(params) => (
              <TextField {...params} sx={fieldSx} label="Log Type" />
            )}
          />
        </Grid>
        <Grid item xs={4} md={2}>
          <Autocomplete
            disableListWrap
            disablePortal
            slotProps={autocompletePopupSx}
            value={`${filter.LogSeverity}`}
            onChange={(e, value) => changeFilter(e, value, "LogSeverity")}
            options={EmployeeLogSeverity.map((item) => ({
              label: `${item}`,
              value: item,
            }))}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={fieldSx}
                label="Greater than Log Severity"
              />
            )}
          />
        </Grid>
      </Grid>

      {loading && (
        <Box className="w-100 h-100 d-flex justify-content-center align-items-center">
          <CircularProgress />
        </Box>
      )}
      {!loading && data && data?.data?.length > 0 ? (
        <>
          <MyTable
            checkboxSelection={false}
            columns={columns}
            lastPage={data.last_page}
            rows={data.data}
            perPage={data.per_page}
            paginationMode="server"
            rowCount={data.total}
            onPaginationModelChange={setPaginationModel}
            onePageOption={true}
            loading={tableLoading}
            hideFooter={true}
            sortType={true}
            sortMode={{
              field: filter?.field,
              sort: filter?.type,
            }}
            handleSortModelChange={handleSortModelChange}
            minHeightRow="auto"
          />
          <br />
          {data.data && data.data.length > 0 && (
            <PaginationComponent results={data} pageChanged={PageChanged} />
          )}
        </>
      ) : (
        !loading && (
          <div className="mt-5">
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{ height: 460 }}
            >
              <img
                src="/images/blur-logo.png"
                style={{ height: 200, width: 250 }}
                alt="No logs"
              />
              <span className="mt-4" style={{ color: "#555555" }}>
                No Logs Found
              </span>
            </div>
          </div>
        )
      )}
    </Card>
  );
};

export default System;
